/**
 * Types pour les charges fixes (frais récurrents entreprise)
 * @module types/charges-fixes.types
 */

// =============================================================================
// Types de base
// =============================================================================

/**
 * Catégories de charges fixes disponibles
 */
export type CategorieChargeFix =
  | 'loyer'
  | 'assurance'
  | 'leasing'
  | 'abonnement'
  | 'energie'
  | 'vehicules'
  | 'emprunt'
  | 'comptable'
  | 'taxes'
  | 'autre';

/**
 * Fréquences de prélèvement disponibles
 */
export type FrequenceChargeFix = 'mensuel' | 'trimestriel' | 'annuel';

// =============================================================================
// Labels et icônes pour l'UI
// =============================================================================

/**
 * Labels des catégories pour affichage UI
 */
export const CATEGORIES_LABELS: Record<CategorieChargeFix, string> = {
  loyer: 'Loyer',
  assurance: 'Assurance',
  leasing: 'Leasing véhicule',
  abonnement: 'Abonnement / Logiciel',
  energie: 'Énergie',
  vehicules: 'Véhicules / Carburant',
  emprunt: 'Emprunt / Crédit',
  comptable: 'Expert-comptable',
  taxes: 'Taxes & impôts',
  autre: 'Autre',
};

/**
 * Icônes Lucide par catégorie
 */
export const CATEGORIES_ICONS: Record<CategorieChargeFix, string> = {
  loyer: 'Home',
  assurance: 'Shield',
  leasing: 'Car',
  abonnement: 'FileText',
  energie: 'Zap',
  vehicules: 'Fuel',
  emprunt: 'Landmark',
  comptable: 'Building2',
  taxes: 'FileSpreadsheet',
  autre: 'Pin',
};

/**
 * Labels des fréquences pour affichage UI
 */
export const FREQUENCES_LABELS: Record<FrequenceChargeFix, string> = {
  mensuel: 'Mensuel',
  trimestriel: 'Trimestriel',
  annuel: 'Annuel',
};

/**
 * Suffixes abrégés des fréquences pour affichage compact
 */
export const FREQUENCES_SUFFIXES: Record<FrequenceChargeFix, string> = {
  mensuel: '/mois',
  trimestriel: '/trim',
  annuel: '/an',
};

// =============================================================================
// Interfaces
// =============================================================================

/**
 * Interface principale d'une charge fixe
 */
export interface ChargeFix {
  id: string;
  entreprise_id: string;
  nom: string;
  categorie: CategorieChargeFix;
  montant_ht: number;
  taux_tva: number;
  montant_ttc: number;
  frequence: FrequenceChargeFix;
  jour_prelevement: number;
  actif: boolean;
  created_at: string;
  updated_at: string;
}

/**
 * Données pour créer une nouvelle charge fixe
 */
export interface CreateChargeFixData {
  nom: string;
  categorie: CategorieChargeFix;
  montant_ht: number;
  taux_tva?: number;
  frequence: FrequenceChargeFix;
  jour_prelevement: number;
}

/**
 * Données pour mettre à jour une charge fixe
 */
export type UpdateChargeFixData = Partial<CreateChargeFixData & { actif: boolean }>;

// =============================================================================
// Return type du hook
// =============================================================================

/**
 * Interface de retour du hook useChargesFixes
 */
export interface UseChargesFixesReturn {
  chargesFixes: ChargeFix[];
  isLoading: boolean;
  error: Error | null;
  totalMensuel: number;
  ajouterChargeFix: (data: CreateChargeFixData) => Promise<void>;
  ajouterChargesFixes: (data: CreateChargeFixData[]) => Promise<boolean>;
  modifierChargeFix: (id: string, data: UpdateChargeFixData) => Promise<void>;
  supprimerChargeFix: (id: string) => Promise<void>;
  toggleActif: (id: string, actif: boolean) => Promise<void>;
  refetch: () => Promise<void>;
}
